/* eslint-disable react/no-unknown-property */
// This file intentionally builds its Three.js JSX tree with React.createElement
// so the Emergent visual-edits babel plugin can't stamp `x-line-number` etc.
// on Three.js host elements (which would blow up R3F v9's applyProps).
import React, { useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const h = React.createElement;

// -----------------------------------------------------------------------------
// Textures — small canvas-generated radial glow sprites.
// -----------------------------------------------------------------------------
const makeGlowTexture = (size = 128) => {
  // Fibre-optic filament tip: intense white-hot core with a tight falloff
  // through pale-blue into transparent. Sharper than a nebula puff — this
  // makes each vertexColor read as a distinct blue OR white dot.
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.12, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(220,235,255,0.85)');
  g.addColorStop(0.7, 'rgba(80,140,255,0.25)');
  g.addColorStop(1, 'rgba(20,60,200,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  return tex;
};

const makeRedGlowTexture = (size = 256) => {
  // Red brand dot: white-hot center -> brand red -> transparent (additive).
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.18, 'rgba(255,220,220,1)');
  g.addColorStop(0.45, 'rgba(239,68,68,0.95)');
  g.addColorStop(1, 'rgba(239,68,68,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  return tex;
};

const makeLaserTexture = () => {
  // Clean radius beam: soft transparent at the centre end, ramping smoothly
  // to a hot white tip. No repeat, no seam — this is a one-shot gradient
  // whose position is driven by the mesh scale, not a scrolling UV.
  const c = document.createElement('canvas');
  c.width = 32;
  c.height = 512;
  const ctx = c.getContext('2d');
  const g = ctx.createLinearGradient(0, 512, 0, 0);
  g.addColorStop(0.0, 'rgba(239,68,68,0)');
  g.addColorStop(0.55, 'rgba(239,68,68,0.55)');
  g.addColorStop(0.90, 'rgba(255,180,180,0.95)');
  g.addColorStop(1.0, 'rgba(255,255,255,1)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 512);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
};

// -----------------------------------------------------------------------------
// Fibonacci-sphere particle distribution + a distant starfield.
// -----------------------------------------------------------------------------
const buildFibonacciPoints = (count, radius) => {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  // Fibre-optic look: crisp near-white filament tips alternating with
  // saturated deep-blue strands. Roughly 50/50 mix with a few outliers
  // for depth, no mid-cyan bleed.
  const white = new THREE.Color('#ffffff');
  const iceBlue = new THREE.Color('#dfefff');
  const brightBlue = new THREE.Color('#2f7dff');
  const deepBlue = new THREE.Color('#0d3fb8');
  const golden = Math.PI * (3 - Math.sqrt(5));
  const tmp = new THREE.Color();
  for (let i = 0; i < count; i += 1) {
    const y = 1 - (i / (count - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const theta = golden * i;
    const x = Math.cos(theta) * r;
    const z = Math.sin(theta) * r;
    positions[i * 3] = x * radius;
    positions[i * 3 + 1] = y * radius;
    positions[i * 3 + 2] = z * radius;
    // Alternating pattern: even index → white/ice, odd → blue. Randomly
    // swap ~15% to break the perfect stripe so the sphere still feels organic.
    const swap = Math.random() < 0.15;
    const isWhite = (i % 2 === 0) !== swap;
    if (isWhite) {
      // 60% pure white filament tips, 40% ice-blue for subtle variation.
      tmp.copy(Math.random() < 0.6 ? white : iceBlue);
    } else {
      // 65% bright blue, 35% deeper blue strands.
      tmp.copy(Math.random() < 0.65 ? brightBlue : deepBlue);
    }
    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }
  return { positions, colors };
};

const buildStarfield = (count, radius) => {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    const r = radius * (0.85 + Math.random() * 0.3);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }
  return positions;
};

// -----------------------------------------------------------------------------
// Scene contents (imperative React.createElement tree)
// -----------------------------------------------------------------------------
const SceneContents = ({ stateRef }) => {
  const globeRef = useRef();
  const haloRef = useRef();
  const ringRef = useRef();
  const dotRef = useRef();
  const laserRef = useRef();
  const coreRef = useRef();

  const { camera } = useThree();
  const dampRef = useRef({ scale: 1, x: 1.35, camZ: 7.4 });

  const dotGlowTex = useMemo(() => makeRedGlowTexture(), []);
  const particleTex = useMemo(() => makeGlowTexture(128), []);
  const laserTex = useMemo(() => makeLaserTexture(), []);

  const { positions: pPositions, colors: pColors } = useMemo(
    () => buildFibonacciPoints(7000, 2.25),
    []
  );

  const starPositions = useMemo(() => buildStarfield(900, 22), []);

  const R = 3.15;

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.05);
    const t = performance.now() / 1000;
    const s = stateRef.current || { progress: 0, arrivalT: 0 };
    const progress = s.progress || 0;
    const arrivalT = s.arrivalT || 0;

    if (globeRef.current) {
      globeRef.current.rotation.y = progress * Math.PI * 2 * 2.4 + t * 0.04;
      globeRef.current.rotation.x = Math.sin(t * 0.15) * 0.08 + progress * 0.2;
      const targetScale = 1 + arrivalT * 0.16;
      dampRef.current.scale += (targetScale - dampRef.current.scale) * Math.min(1, dt * 4);
      globeRef.current.scale.setScalar(dampRef.current.scale);
      const targetX = 1.35 * (1 - arrivalT);
      dampRef.current.x += (targetX - dampRef.current.x) * Math.min(1, dt * 4);
      globeRef.current.position.x = dampRef.current.x;
      if (haloRef.current) haloRef.current.position.x = dampRef.current.x;
      if (coreRef.current) coreRef.current.position.x = dampRef.current.x;
      if (ringRef.current) ringRef.current.position.x = dampRef.current.x;
    }
    if (haloRef.current) {
      haloRef.current.rotation.y = progress * Math.PI * 2 * 2.4 + t * 0.04;
      haloRef.current.rotation.x = Math.sin(t * 0.15) * 0.08 + progress * 0.2;
    }
    if (coreRef.current) {
      const pulse = 0.9 + Math.sin(t * 1.4) * 0.08;
      coreRef.current.scale.setScalar(pulse);
      if (coreRef.current.material) {
        coreRef.current.material.opacity = 0.4 + Math.sin(t * 1.4) * 0.06;
      }
    }

    if (dotRef.current) {
      // Orbit in the ring's local XY plane — the same plane a THREE.TorusGeometry
      // is authored in — so the dot rides the torus exactly, spinning with the
      // ring group's tilt automatically.
      const theta = progress * Math.PI * 2 * 2.4 + t * 0.04;
      const lx = Math.cos(theta) * R;
      const ly = Math.sin(theta) * R;
      dotRef.current.position.set(lx, ly, 0);
      // Billboard — face the camera in world space.
      dotRef.current.lookAt(camera.position);
      const pulse = 0.85 + Math.sin(t * 3.2) * 0.15 + arrivalT * 0.4;
      dotRef.current.scale.setScalar(pulse);
      if (dotRef.current.material) {
        dotRef.current.material.opacity = 0.75 + arrivalT * 0.25;
      }

      if (laserRef.current) {
        // Laser is a group of two cylinders (hot core + soft bloom) that share
        // transform. We aim it centre -> dot in the ring's LOCAL xy plane.
        const center = new THREE.Vector3(0, 0, 0);
        const target = new THREE.Vector3(lx, ly, 0);
        const dir = target.clone().sub(center);
        const length = dir.length();
        const yLen = length * arrivalT;
        laserRef.current.position.copy(center.clone().lerp(target, arrivalT / 2));
        laserRef.current.scale.set(1, Math.max(0.0001, yLen), 1);
        const q = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          dir.normalize()
        );
        laserRef.current.quaternion.copy(q);
        // Subtle idle shimmer so the beam feels alive without pulsing
        // aggressively. Both children get animated.
        const shimmer = 0.9 + Math.sin(t * 2.6) * 0.1;
        const coreOpacity = arrivalT * shimmer;
        const bloomOpacity = arrivalT * 0.35 * shimmer;
        laserRef.current.children.forEach((child, idx) => {
          if (child.material) {
            child.material.opacity = idx === 0 ? coreOpacity : bloomOpacity;
          }
        });
      }
    }

    const camTarget = 7.4 - arrivalT * 2.9;
    dampRef.current.camZ += (camTarget - dampRef.current.camZ) * Math.min(1, dt * 4);
    camera.position.z = dampRef.current.camZ;
    camera.updateProjectionMatrix();
  });

  // ---- Build the R3F tree with React.createElement (no JSX). --------------
  const starGeom = h('bufferGeometry', null,
    h('bufferAttribute', {
      attach: 'attributes-position',
      count: starPositions.length / 3,
      array: starPositions,
      itemSize: 3,
    })
  );
  const starMat = h('pointsMaterial', {
    size: 0.03,
    color: '#9fc4e8',
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
    sizeAttenuation: true,
  });
  const stars = h('points', null, starGeom, starMat);

  const core = h(
    'mesh',
    { ref: coreRef, position: [0, 0, 0] },
    h('sphereGeometry', { args: [1.6, 32, 32] }),
    h('meshBasicMaterial', {
      // Brighter inner core so the sphere reads with a clear luminous
      // heart under the fibre-optic dots.
      color: '#3d8fff',
      transparent: true,
      opacity: 0.42,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  );

  const pointsGeom = h('bufferGeometry', null,
    h('bufferAttribute', {
      attach: 'attributes-position',
      count: pPositions.length / 3,
      array: pPositions,
      itemSize: 3,
    }),
    h('bufferAttribute', {
      attach: 'attributes-color',
      count: pColors.length / 3,
      array: pColors,
      itemSize: 3,
    })
  );

  const haloGeom = h('bufferGeometry', null,
    h('bufferAttribute', {
      attach: 'attributes-position',
      count: pPositions.length / 3,
      array: pPositions,
      itemSize: 3,
    })
  );

  const haloPoints = h(
    'points',
    { ref: haloRef, position: [1.35, 0, 0] },
    haloGeom,
    h('pointsMaterial', {
      // Larger + brighter halo layer creates a static bloom around every
      // fibre-optic dot. Combined with the sharp inner sprite this reads as
      // a permanently-lit particle rather than a plain point.
      size: 0.42,
      map: particleTex,
      alphaMap: particleTex,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
      color: '#4a95ff',
    })
  );

  const globePoints = h(
    'points',
    { ref: globeRef, position: [1.35, 0, 0] },
    pointsGeom,
    h('pointsMaterial', {
      size: 0.06,
      map: particleTex,
      alphaMap: particleTex,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    })
  );

  const ringGroup = h(
    'group',
    { ref: ringRef, rotation: [1.15, 0, 0.18], position: [1.35, 0, 0] },
    // Thin near-white orbital ring matching the reference image.
    h('mesh', null,
      h('torusGeometry', { args: [R, 0.006, 16, 260] }),
      h('meshBasicMaterial', {
        color: '#dbeeff', transparent: true, opacity: 0.55,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    ),
    // Red brand dot — additive plane, billboarded via lookAt in useFrame.
    h('mesh', { ref: dotRef, scale: 0.9 },
      h('planeGeometry', { args: [0.55, 0.55] }),
      h('meshBasicMaterial', {
        map: dotGlowTex,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    ),
    // Beam group holds the ref for scaling/positioning both cylinders together.
    h('group', { ref: laserRef, position: [0, 0, 0] },
      // Inner hot core — a very thin, uniform-radius cylinder that renders
      // the sharp red-to-white beam.
      h('mesh', null,
        h('cylinderGeometry', { args: [0.012, 0.012, 1, 20, 1, true] }),
        h('meshBasicMaterial', {
          map: laserTex,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          depthTest: false,
          blending: THREE.AdditiveBlending,
          side: THREE.DoubleSide,
        })
      ),
      // Outer bloom — a wider, softer cylinder that adds a subtle glow halo
      // around the core beam. Additive so it never looks like a chunky pipe.
      h('mesh', null,
        h('cylinderGeometry', { args: [0.045, 0.045, 1, 20, 1, true] }),
        h('meshBasicMaterial', {
          map: laserTex,
          color: '#ff6b6b',
          transparent: true,
          opacity: 0,
          depthWrite: false,
          depthTest: false,
          blending: THREE.AdditiveBlending,
          side: THREE.DoubleSide,
        })
      )
    )
  );

  return h(React.Fragment, null, stars, core, haloPoints, globePoints, ringGroup);
};

export const ParticleGlobe = ({ stateRef }) => (
  h(Canvas, {
    dpr: [1, 1.75],
    camera: { position: [0, 0, 7.4], fov: 42 },
    gl: { alpha: true, antialias: true, powerPreference: 'high-performance' },
    style: { background: 'transparent' },
  }, h(SceneContents, { stateRef }))
);

export default ParticleGlobe;
